import React, { useState, useEffect } from 'react';
import { Connection, PublicKey } from '@solana/web3.js';
import { Program, AnchorProvider } from '@coral-xyz/anchor';
import ZnoospaceIDL from '../idl/chirpx_solana.json';
import { ZNOOSPACE_PROGRAM_ID } from '../services/solanaService';
import { SolanaService } from '../services/solanaService';

const DiagnosticTest = () => {
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [overallStatus, setOverallStatus] = useState('pending');

  const addResult = (test, status, message, details = null) => {
    setTestResults(prev => [...prev, { test, status, message, details, timestamp: new Date().toISOString() }]);
  };

  const runDiagnostics = async () => {
    setIsRunning(true);
    setTestResults([]);
    setOverallStatus('running');

    try {
      // Test 1: Check if IDL is properly loaded
      addResult('IDL Loading', 'running', 'Checking IDL file...');
      if (ZnoospaceIDL && ZnoospaceIDL.instructions && ZnoospaceIDL.instructions.length > 0) {
        addResult('IDL Loading', 'success', `IDL loaded successfully with ${ZnoospaceIDL.instructions.length} instructions`, {
          programName: ZnoospaceIDL.metadata?.name,
          version: ZnoospaceIDL.metadata?.version,
          address: ZnoospaceIDL.address
        });
      } else {
        addResult('IDL Loading', 'error', 'IDL file is missing or malformed', ZnoospaceIDL);
        setOverallStatus('failed');
        return;
      }

      // Test 2: Check Program ID
      addResult('Program ID', 'running', 'Validating program ID...');
      try {
        const programId = new PublicKey(ZNOOSPACE_PROGRAM_ID);
        addResult('Program ID', 'success', `Program ID is valid: ${programId.toString()}`);
      } catch (error) {
        addResult('Program ID', 'error', 'Invalid program ID format', error.message);
        setOverallStatus('failed');
        return;
      }

      // Test 3: Check RPC Connection
      addResult('RPC Connection', 'running', 'Testing Solana RPC connection...');
      try {
        const connection = new Connection('https://api.devnet.solana.com');
        const version = await connection.getVersion();
        addResult('RPC Connection', 'success', `Connected to Solana devnet`, {
          solanaCore: version['solana-core'],
          featureSet: version['feature-set']
        });
      } catch (error) {
        addResult('RPC Connection', 'error', 'Failed to connect to Solana RPC', error.message);
        setOverallStatus('failed');
        return;
      }

      // Test 4: Check Wallet Availability
      addResult('Wallet Detection', 'running', 'Checking for available wallets...');
      const wallets = [];
      if (window.solana && window.solana.isPhantom) wallets.push('Phantom');
      if (window.nightly) wallets.push('Nightly');
      if (window.solflare && window.solflare.isSolflare) wallets.push('Solflare');
      
      if (wallets.length > 0) {
        addResult('Wallet Detection', 'success', `Found ${wallets.length} wallet(s): ${wallets.join(', ')}`);
      } else {
        addResult('Wallet Detection', 'warning', 'No wallets detected. Please install a Solana wallet.');
      }

      // Test 5: Test Wallet Connection (if available)
      let connectedWallet = null;
      if (wallets.length > 0) {
        addResult('Wallet Connection', 'running', 'Testing wallet connection...');
        try {
          let walletProvider = null;
          if (window.solana && window.solana.isPhantom) {
            walletProvider = window.solana;
          } else if (window.nightly) {
            walletProvider = window.nightly;
          } else if (window.solflare) {
            walletProvider = window.solflare;
          }

          if (walletProvider) {
            // Try to connect (this might prompt user)
            const response = await walletProvider.connect();
            if (response.publicKey) {
              connectedWallet = {
                publicKey: response.publicKey,
                signTransaction: walletProvider.signTransaction?.bind(walletProvider),
                signAllTransactions: walletProvider.signAllTransactions?.bind(walletProvider),
              };
              addResult('Wallet Connection', 'success', `Wallet connected: ${response.publicKey.toString()}`);
            } else {
              addResult('Wallet Connection', 'error', 'Wallet connected but no public key available');
            }
          }
        } catch (error) {
          addResult('Wallet Connection', 'warning', 'Wallet connection failed or cancelled', error.message);
        }
      }

      // Test 6: Test Anchor Provider Creation
      if (connectedWallet) {
        addResult('Anchor Provider', 'running', 'Creating Anchor provider...');
        try {
          const connection = new Connection('https://api.devnet.solana.com');
          const provider = new AnchorProvider(
            connection,
            connectedWallet,
            { commitment: 'confirmed', preflightCommitment: 'confirmed' }
          );
          addResult('Anchor Provider', 'success', 'Anchor provider created successfully');

          // Test 7: Anchor Program (or Direct Transaction Mode)
          addResult('Anchor Program', 'running', 'Testing contract integration...');
          try {
            // Try to create SolanaService and see if it initializes properly
            const testService = new SolanaService(connection, connectedWallet);
            
            if (testService.usingDirectMode) {
              // Direct transaction mode is working
              addResult('Anchor Program', 'success', 'Contract integration ready (Direct Transaction Mode)', {
                details: 'Using direct transactions instead of Anchor Program due to IDL compatibility. All contract methods available.'
              });
            } else if (testService.program && testService.program.methods) {
              // Anchor program mode is working
              addResult('Anchor Program', 'success', 'Anchor program created successfully', {
                details: `Methods available: ${Object.keys(testService.program.methods).join(', ')}`
              });
            } else {
              throw new Error('Neither Anchor program nor direct mode is working');
            }
          } catch (error) {
            addResult('Anchor Program', 'error', 'Failed to initialize contract integration', {
              details: {
                message: error.message,
                stack: error.stack ? error.stack.split('\n').slice(0, 3) : []
              }
            });
          }
        } catch (error) {
          addResult('Anchor Provider', 'error', 'Failed to create Anchor provider', {
            message: error.message,
            stack: error.stack?.split('\n').slice(0, 3)
          });
          setOverallStatus('failed');
        }
      } else {
        addResult('Anchor Provider', 'skipped', 'Skipped - no wallet connected');
        addResult('Anchor Program', 'skipped', 'Skipped - no wallet connected');
        setOverallStatus('partial');
      }

    } catch (error) {
      addResult('Diagnostic Test', 'error', 'Unexpected error during diagnostics', error.message);
      setOverallStatus('failed');
    } finally {
      setIsRunning(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'running': return '🔄';
      case 'skipped': return '⏭️';
      default: return '⏳';
    }
  };

  const getOverallStatusColor = () => {
    switch (overallStatus) {
      case 'success': return '#4CAF50';
      case 'failed': return '#f44336';
      case 'partial': return '#FF9800';
      case 'running': return '#2196F3';
      default: return '#9E9E9E';
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto', fontFamily: 'monospace' }}>
      <h2>🔧 Znoospace Solana Integration Diagnostics</h2>
      
      <div style={{ 
        padding: '15px', 
        marginBottom: '20px', 
        border: `2px solid ${getOverallStatusColor()}`,
        borderRadius: '8px',
        backgroundColor: '#f5f5f5'
      }}>
        <h3>Overall Status: <span style={{ color: getOverallStatusColor() }}>{overallStatus.toUpperCase()}</span></h3>
        <button 
          onClick={runDiagnostics} 
          disabled={isRunning}
          style={{
            padding: '10px 20px',
            backgroundColor: isRunning ? '#ccc' : '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isRunning ? 'not-allowed' : 'pointer'
          }}
        >
          {isRunning ? '🔄 Running Diagnostics...' : '🚀 Run Full Diagnostic Test'}
        </button>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Test Results:</h3>
        {testResults.length === 0 && !isRunning && (
          <p style={{ color: '#666' }}>Click "Run Full Diagnostic Test" to start testing...</p>
        )}
        
        {testResults.map((result, index) => (
          <div key={index} style={{
            padding: '10px',
            margin: '5px 0',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: result.status === 'error' ? '#ffebee' : 
                           result.status === 'success' ? '#e8f5e8' :
                           result.status === 'warning' ? '#fff3e0' : '#f5f5f5'
          }}>
            <div style={{ fontWeight: 'bold' }}>
              {getStatusIcon(result.status)} {result.test}
            </div>
            <div style={{ marginLeft: '20px', color: '#555' }}>
              {result.message}
            </div>
            {result.details && (
              <details style={{ marginLeft: '20px', marginTop: '5px' }}>
                <summary style={{ cursor: 'pointer', color: '#666' }}>Details</summary>
                <pre style={{ 
                  backgroundColor: '#f0f0f0', 
                  padding: '10px', 
                  borderRadius: '4px',
                  fontSize: '12px',
                  overflow: 'auto'
                }}>
                  {JSON.stringify(result.details, null, 2)}
                </pre>
              </details>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '8px' }}>
        <h4>💡 What This Test Does:</h4>
        <ul>
          <li><strong>IDL Loading:</strong> Verifies the smart contract interface is properly loaded</li>
          <li><strong>Program ID:</strong> Validates the deployed contract address</li>
          <li><strong>RPC Connection:</strong> Tests connection to Solana network</li>
          <li><strong>Wallet Detection:</strong> Finds available Solana wallets</li>
          <li><strong>Wallet Connection:</strong> Tests wallet connectivity</li>
          <li><strong>Anchor Provider:</strong> Tests Anchor framework provider creation</li>
          <li><strong>Anchor Program:</strong> Tests smart contract program initialization</li>
        </ul>
        <p><strong>Goal:</strong> Identify exactly why "Anchor program not initialized" error occurs and fix it!</p>
      </div>
    </div>
  );
};

export default DiagnosticTest;
